// src/stock/ImportProduits.jsx
import React, { useState } from "react";
import {
    Card, Row, Col, Form, Button, Table,
    Spinner, Badge
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import * as XLSX from 'xlsx';

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ImportProduits() {
    const navigate = useNavigate();
    const [importStep, setImportStep] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState([]);
    const [validationResults, setValidationResults] = useState({});
    const [importProgress, setImportProgress] = useState(0);
    const [importStatus, setImportStatus] = useState('idle');
    const [importStats, setImportStats] = useState({});
    const [importing, setImporting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    const expectedColumns = [
        { key: 'reference', label: 'Référence', required: false, example: 'REF2024001' },
        { key: 'designation', label: 'Désignation', required: true, example: 'Produit test' },
        { key: 'description', label: 'Description', required: false, example: 'Description du produit' },
        { key: 'prix_achat_ht', label: 'Prix achat HT', required: true, example: '1000' },
        { key: 'prix_vente_ht', label: 'Prix vente HT', required: true, example: '1500' },
        { key: 'seuil_alerte', label: 'Seuil alerte', required: false, example: '5' },
        { key: 'stock_initial', label: 'Stock initial', required: false, example: '10' }
    ];

    /* ============================================================
       TEMPLATE
       ============================================================ */
    const downloadTemplate = () => {
        const data = [
            ['reference', 'designation', 'description', 'prix_achat_ht', 'prix_vente_ht', 'seuil_alerte', 'stock_initial'],
            ['REF001', 'Produit exemple 1', 'Description produit 1', '1000', '1500', '5', '10'],
            ['REF002', 'Produit exemple 2', 'Description produit 2', '2000', '2500', '3', '5'],
        ];
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Produits");
        XLSX.writeFile(workbook, "modele-import-produits.xlsx");
        showToast('info', 'Modèle téléchargé', 'Le fichier modèle a été téléchargé');
    };

    /* ============================================================
       FILE HANDLING
       ============================================================ */
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.csv', '.xls', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            showToast('error', 'Format invalide', 'Veuillez sélectionner un fichier CSV ou Excel valide');
            return;
        }

        setSelectedFile(file);
        previewFile(file);
    };

    const previewFile = (file) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                let previewData = [];

                if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
                    const content = e.target.result;
                    const lines = content.split('\n').slice(0, 11);
                    previewData = lines.map(line =>
                        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
                    );
                } else {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }).slice(0, 11);
                }

                setFilePreview(previewData);
                validateFile(previewData);
            } catch (error) {
                console.error('Erreur lecture fichier:', error);
                showToast('error', 'Erreur de lecture', 'Impossible de lire le fichier sélectionné');
            }
        };

        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    const validateFile = (previewData) => {
        const results = {
            valid: 0,
            invalid: 0,
            errors: [],
            columnMatch: true,
            produits: []
        };

        if (previewData.length > 0) {
            const headers = previewData[0].map(h => h?.toString().toLowerCase().trim());
            const requiredHeaders = ['designation', 'prix_achat_ht', 'prix_vente_ht'];

            requiredHeaders.forEach(required => {
                if (!headers.includes(required)) {
                    results.columnMatch = false;
                    results.errors.push(`Colonne '${required}' manquante`);
                }
            });

            const colIndex = {
                reference: headers.indexOf('reference'),
                designation: headers.indexOf('designation'),
                description: headers.indexOf('description'),
                prix_achat_ht: headers.indexOf('prix_achat_ht'),
                prix_vente_ht: headers.indexOf('prix_vente_ht'),
                seuil_alerte: headers.indexOf('seuil_alerte'),
                stock_initial: headers.indexOf('stock_initial')
            };

            if (results.columnMatch) {
                previewData.slice(1).forEach((row, index) => {
                    const ligne = index + 2;

                    if (!row[colIndex.designation] || !row[colIndex.prix_achat_ht] || !row[colIndex.prix_vente_ht]) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Champs obligatoires manquants`);
                    }
                    else if (isNaN(parseFloat(row[colIndex.prix_achat_ht])) || parseFloat(row[colIndex.prix_achat_ht]) <= 0) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Prix achat invalide`);
                    }
                    else if (isNaN(parseFloat(row[colIndex.prix_vente_ht])) || parseFloat(row[colIndex.prix_vente_ht]) <= 0) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Prix vente invalide`);
                    }
                    else {
                        const produit = {
                            reference: colIndex.reference !== -1 ? row[colIndex.reference]?.toString().trim() || '' : '',
                            designation: row[colIndex.designation]?.toString().trim(),
                            description: colIndex.description !== -1 ? row[colIndex.description]?.toString().trim() || '' : '',
                            prix_achat_ht: parseFloat(row[colIndex.prix_achat_ht]),
                            prix_vente_ht: parseFloat(row[colIndex.prix_vente_ht]),
                            seuil_alerte: colIndex.seuil_alerte !== -1 ? parseInt(row[colIndex.seuil_alerte]) || 5 : 5,
                            stock_initial: colIndex.stock_initial !== -1 ? parseInt(row[colIndex.stock_initial]) || 0 : 0
                        };
                        results.produits.push(produit);
                        results.valid++;
                    }
                });
            }
        }

        setValidationResults(results);
        setImportStep(2);
    };

    /* ============================================================
       IMPORT
       ============================================================ */
    const importProduits = async () => {
        setImporting(true);
        setImportStatus('importing');
        setImportProgress(0);

        try {
            const token = localStorage.getItem("token");

            const progressInterval = setInterval(() => {
                setImportProgress(prev => Math.min(prev + 10, 90));
            }, 300);

            const results = {
                total: validationResults.produits.length,
                imported: 0,
                failed: 0,
                errors: []
            };

            for (let i = 0; i < validationResults.produits.length; i++) {
                const produit = validationResults.produits[i];

                try {
                    const response = await fetch(
                        `${API_BASE_URL}/produits.php`,
                        {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(produit)
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        results.imported++;
                    } else {
                        results.failed++;
                        results.errors.push(`Produit ${produit.designation}: ${result.message || 'Erreur inconnue'}`);
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push(`Produit ${produit.designation}: Erreur de connexion`);
                }

                setImportProgress(Math.floor(((i + 1) / validationResults.produits.length) * 100));
            }

            clearInterval(progressInterval);
            setImportProgress(100);

            setImportStats(results);
            setImportStatus(results.failed === 0 ? 'success' : results.imported > 0 ? 'partial' : 'error');

            if (results.failed === 0) {
                showToast('success', 'Import réussi', `${results.imported} produits importés avec succès`);
            } else if (results.imported > 0) {
                showToast('warning', 'Import partiel', `${results.imported} importés, ${results.failed} échecs`);
            } else {
                showToast('error', 'Import échoué', "Aucun produit n'a pu être importé");
            }

        } catch (error) {
            console.error("Erreur import:", error);
            setImportStatus('error');
            showToast('error', 'Erreur', error.message || "Erreur lors de l'import");
        } finally {
            setImporting(false);
            setImportStep(3);
        }
    };

    const resetImport = () => {
        setImportStep(1);
        setSelectedFile(null);
        setFilePreview([]);
        setValidationResults({});
        setImportStats({});
        setImportProgress(0);
        setImportStatus('idle');
    };

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-upload-cloud-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Importer des produits</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-excel-2-line"></i>
                                        Excel ou CSV
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={downloadTemplate}>
                                <i className="ri-download-line"></i> Télécharger le modèle
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/produits')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== ÉTAPES ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-4">
                            <div className="steps-container">
                                <div className={`step-item ${importStep >= 1 ? 'active' : ''}`}>
                                    <div className="step-circle">1</div>
                                    <div className="step-label">Fichier</div>
                                </div>
                                <div className={`step-line ${importStep >= 2 ? 'active' : ''}`}></div>
                                <div className={`step-item ${importStep >= 2 ? 'active' : ''}`}>
                                    <div className="step-circle">2</div>
                                    <div className="step-label">Validation</div>
                                </div>
                                <div className={`step-line ${importStep >= 3 ? 'active' : ''}`}></div>
                                <div className={`step-item ${importStep >= 3 ? 'active' : ''}`}>
                                    <div className="step-circle">3</div>
                                    <div className="step-label">Résultats</div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== ÉTAPE 1: UPLOAD ===== */}
                    {importStep === 1 && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="card-header-gradient">
                                <h5 className="mb-0 text-white d-flex align-items-center">
                                    <i className="ri-file-upload-line me-2"></i>
                                    Sélectionner le fichier
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <div className="upload-area-charte text-center p-5">
                                    <i className="ri-file-upload-line upload-icon-charte mb-3"></i>
                                    <h5 className="fw-bold">Glissez-déposez votre fichier</h5>
                                    <p className="text-muted mb-4">
                                        Formats supportés : CSV, Excel (.xlsx, .xls)
                                    </p>
                                    <Form.Group>
                                        <Form.Control
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={handleFileSelect}
                                            className="d-none"
                                            id="fileInput"
                                        />
                                        <Button
                                            className="btn-modern-primary"
                                            onClick={() => document.getElementById('fileInput').click()}
                                        >
                                            <i className="ri-folder-open-line me-2"></i>
                                            Parcourir
                                        </Button>
                                    </Form.Group>
                                </div>

                                <div className="mt-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-information-line"></i>
                                        </span>
                                        <h6>Format attendu</h6>
                                    </div>
                                    <div className="table-responsive">
                                        <Table className="align-middle mb-0 table-charte">
                                            <thead>
                                                <tr>
                                                    <th>Colonne</th>
                                                    <th className="text-center">Obligatoire</th>
                                                    <th>Exemple</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {expectedColumns.map(col => (
                                                    <tr key={col.key}>
                                                        <td className="fw-semibold">{col.label}</td>
                                                        <td className="text-center">
                                                            {col.required ? (
                                                                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                                                    <i className="ri-error-warning-line"></i>Obligatoire
                                                                </span>
                                                            ) : (
                                                                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, #64748b, #475569)` }}>
                                                                    <i className="ri-checkbox-blank-circle-line"></i>Optionnel
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td><span className="reference-badge-charte">{col.example}</span></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== ÉTAPE 2: VALIDATION ===== */}
                    {importStep === 2 && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="card-header-gradient">
                                <h5 className="mb-0 text-white d-flex align-items-center">
                                    <i className="ri-checkbox-circle-line me-2"></i>
                                    Validation des données
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {importing ? (
                                    <div className="text-center py-4">
                                        <div className="loading-spinner-charte mb-3">
                                            <div className="loading-ring-charte"></div>
                                            <i className="ri-upload-cloud-2-line loading-icon-charte"></i>
                                        </div>
                                        <h5 className="mb-3">Import en cours...</h5>
                                        <div className="progress-bar-charte-custom mb-3" style={{ width: '100%', height: '8px' }}>
                                            <div className="progress-fill-charte" style={{ width: `${importProgress}%`, background: COLORS.primary }}></div>
                                        </div>
                                        <p className="text-muted">
                                            Traitement de {validationResults.produits?.length || 0} produits... {importProgress}%
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {/* KPI */}
                                        <Row className="g-3 mb-4">
                                            <Col sm="4">
                                                <div className="kpi-charte kpi-success">
                                                    <div className="kpi-icon-charte"><i className="ri-check-line"></i></div>
                                                    <div className="kpi-content-charte">
                                                        <span className="kpi-label-charte">Produits valides</span>
                                                        <h3 className="kpi-value-charte">{validationResults.valid || 0}</h3>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col sm="4">
                                                <div className="kpi-charte kpi-danger">
                                                    <div className="kpi-icon-charte"><i className="ri-close-line"></i></div>
                                                    <div className="kpi-content-charte">
                                                        <span className="kpi-label-charte">Erreurs</span>
                                                        <h3 className="kpi-value-charte">{validationResults.invalid || 0}</h3>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col sm="4">
                                                <div className="kpi-charte kpi-primary">
                                                    <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                                    <div className="kpi-content-charte">
                                                        <span className="kpi-label-charte">Total lignes</span>
                                                        <h3 className="kpi-value-charte">{filePreview.length - 1 || 0}</h3>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Aperçu */}
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                <i className="ri-table-line"></i>
                                            </span>
                                            <h6>Aperçu des données</h6>
                                        </div>
                                        <div className="table-responsive mb-4">
                                            <Table className="align-middle mb-0 table-charte">
                                                <thead>
                                                    <tr>
                                                        {filePreview[0]?.map((h, i) => (
                                                            <th key={i}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filePreview.slice(1, 6).map((row, i) => (
                                                        <tr key={i}>
                                                            {row.map((cell, j) => (
                                                                <td key={j}>{cell || '—'}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>

                                        {/* Erreurs */}
                                        {validationResults.errors?.length > 0 && (
                                            <div className="mb-4">
                                                <div className="section-title-charte">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                                        <i className="ri-error-warning-line"></i>
                                                    </span>
                                                    <h6>Erreurs détectées</h6>
                                                </div>
                                                <div className="alert-error-charte" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                                    {validationResults.errors.slice(0, 10).map((err, i) => (
                                                        <div key={i} className="small text-danger mb-1 d-flex align-items-start">
                                                            <i className="ri-close-circle-line me-2 mt-1"></i>
                                                            <span>{err}</span>
                                                        </div>
                                                    ))}
                                                    {validationResults.errors.length > 10 && (
                                                        <small className="text-muted">
                                                            ... et {validationResults.errors.length - 10} autres erreurs
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="d-flex justify-content-between flex-wrap gap-2">
                                            <Button className="btn-modern-outline" onClick={() => setImportStep(1)}>
                                                <i className="ri-arrow-left-line me-1"></i> Retour
                                            </Button>
                                            <Button
                                                className="btn-modern-primary"
                                                onClick={importProduits}
                                                disabled={validationResults.valid === 0 || importing}
                                            >
                                                <i className="ri-check-line me-2"></i>
                                                Confirmer l'import ({validationResults.valid})
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== ÉTAPE 3: RÉSULTATS ===== */}
                    {importStep === 3 && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header
                                className="card-header-gradient"
                                style={{
                                    background: importStatus === 'success'
                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                        : importStatus === 'partial'
                                            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                }}
                            >
                                <h5 className="mb-0 text-white d-flex align-items-center">
                                    <i className={`ri-${importStatus === 'success' ? 'checkbox-circle' : importStatus === 'partial' ? 'error-warning' : 'close-circle'}-line me-2`}></i>
                                    {importStatus === 'success' && 'Import réussi'}
                                    {importStatus === 'partial' && 'Import partiel'}
                                    {importStatus === 'error' && 'Erreur'}
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <div className="text-center mb-4">
                                    <div className="result-icon-charte mx-auto mb-3" style={{
                                        background: importStatus === 'success'
                                            ? `${COLORS.success}15`
                                            : importStatus === 'partial'
                                                ? `${COLORS.warning}15`
                                                : `${COLORS.danger}15`,
                                        color: importStatus === 'success'
                                            ? COLORS.success
                                            : importStatus === 'partial'
                                                ? COLORS.warning
                                                : COLORS.danger
                                    }}>
                                        <i className={`ri-${importStatus === 'success' ? 'checkbox-circle' : importStatus === 'partial' ? 'error-warning' : 'close-circle'}-line`}></i>
                                    </div>

                                    {importStatus === 'success' && (
                                        <>
                                            <h4 className="fw-bold">Import terminé avec succès</h4>
                                            <p className="text-muted mb-0">{importStats.imported} produits importés</p>
                                        </>
                                    )}

                                    {importStatus === 'partial' && (
                                        <>
                                            <h4 className="fw-bold">Import partiel</h4>
                                            <p className="text-muted mb-0">
                                                {importStats.imported} produits importés • {importStats.failed} échecs
                                            </p>
                                        </>
                                    )}

                                    {importStatus === 'error' && (
                                        <>
                                            <h4 className="fw-bold">Import échoué</h4>
                                            <p className="text-muted mb-0">Aucun produit n'a pu être importé</p>
                                        </>
                                    )}
                                </div>

                                {/* Stats */}
                                {importStats.total > 0 && (
                                    <Row className="g-3 mb-4">
                                        <Col sm="4">
                                            <div className="kpi-charte kpi-primary">
                                                <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                                <div className="kpi-content-charte">
                                                    <span className="kpi-label-charte">Total</span>
                                                    <h3 className="kpi-value-charte">{importStats.total}</h3>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm="4">
                                            <div className="kpi-charte kpi-success">
                                                <div className="kpi-icon-charte"><i className="ri-check-line"></i></div>
                                                <div className="kpi-content-charte">
                                                    <span className="kpi-label-charte">Importés</span>
                                                    <h3 className="kpi-value-charte">{importStats.imported}</h3>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm="4">
                                            <div className="kpi-charte kpi-danger">
                                                <div className="kpi-icon-charte"><i className="ri-close-line"></i></div>
                                                <div className="kpi-content-charte">
                                                    <span className="kpi-label-charte">Échecs</span>
                                                    <h3 className="kpi-value-charte">{importStats.failed}</h3>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {/* Détails erreurs */}
                                {importStats.errors?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                                <i className="ri-error-warning-line"></i>
                                            </span>
                                            <h6>Détails des erreurs</h6>
                                        </div>
                                        <div className="alert-error-charte" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                            {importStats.errors.slice(0, 10).map((err, i) => (
                                                <div key={i} className="small text-danger mb-1 d-flex align-items-start">
                                                    <i className="ri-close-circle-line me-2 mt-1"></i>
                                                    <span>{err}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex gap-2 justify-content-center flex-wrap">
                                    <Button className="btn-modern-outline" onClick={() => navigate('/stock/produits')}>
                                        <i className="ri-list-check me-1"></i> Voir les produits
                                    </Button>
                                    <Button className="btn-modern-primary" onClick={resetImport}>
                                        <i className="ri-refresh-line me-1"></i> Nouvel import
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== ÉTAPES ===== */
                .steps-container {
                    display: flex; align-items: center; justify-content: center;
                    gap: 0.5rem;
                }
                .step-item { text-align: center; }
                .step-circle {
                    width: 44px; height: 44px; border-radius: 50%;
                    background: #f1f5f9; color: #64748b;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 0.5rem;
                    font-weight: 700; font-size: 1rem;
                    transition: all 0.3s ease;
                    border: 2px solid transparent;
                }
                .step-item.active .step-circle {
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    color: white;
                    box-shadow: 0 4px 12px rgba(33, 172, 205, 0.3);
                    border-color: transparent;
                }
                .step-label {
                    font-size: 0.75rem; color: #64748b;
                    font-weight: 600; text-transform: uppercase;
                    letter-spacing: 0.3px;
                }
                .step-item.active .step-label {
                    color: var(--primary);
                }
                .step-line {
                    width: 80px; height: 2px;
                    background: #e2e8f0;
                    transition: background 0.3s ease;
                }
                .step-line.active {
                    background: linear-gradient(90deg, var(--primary), var(--secondary));
                }

                /* ===== UPLOAD ===== */
                .upload-area-charte {
                    border: 2px dashed #cbd5e1;
                    border-radius: 16px;
                    background: #f8fafc;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .upload-area-charte:hover {
                    border-color: var(--secondary);
                    background: white;
                }
                .upload-icon-charte {
                    font-size: 4rem;
                    color: var(--secondary);
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte-custom {
                    background: #e2e8f0;
                    border-radius: 8px;
                    overflow: hidden;
                    display: block;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 8px;
                    transition: width 0.3s ease;
                }

                /* ===== RESULT ICON ===== */
                .result-icon-charte {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 2.5rem;
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.25rem;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.05);
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .step-line { width: 40px; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}